import { useCallback, useEffect, useRef } from 'react';
import { useApp } from '@context/AppContext';
import { useFirebase } from '@/App';
import { assignZones } from './corpseAssignment';

/**
 * Hook providing all Exquisite Corpse game actions.
 * Host-driven: the host client manages timers and phase transitions.
 */
export function useCorpse() {
  const { state, dispatch } = useApp();
  const firebase = useFirebase();
  const { user, room, game } = state;
  const advancingRef = useRef(false);

  const zoneCount = game.zoneCount || 3;

  // Host: initialise corpse with zone assignments
  const initCorpse = useCallback(async ({ zoneDuration, zoneCount: zc }) => {
    const count = zc || 3;
    const memberIds = Object.keys(room.members || {});
    const { creatures, totalCreatures } = assignZones(memberIds, room.members || {}, count);

    await firebase.updateGameState({
      zoneDuration: zoneDuration || 60,
      timerDuration: zoneDuration || 60,
      zoneCount: count,
      creatures,
      totalCreatures,
      currentCreature: 0,
      currentZone: 0,
      currentDrawerId: creatures[0].zones[0].assignedTo,
      timerEndsAt: null,
    });
  }, [firebase, room.members]);

  // Host: start the corpse game — show first assigning overlay
  const startCorpse = useCallback(async () => {
    const creature = game.creatures?.[0];
    if (!creature) return;

    await firebase.updateGameState({
      phase: 'assigning',
      currentCreature: 0,
      currentZone: 0,
      currentDrawerId: creature.zones[0].assignedTo,
      timerEndsAt: null,
    });
  }, [firebase, game.creatures]);

  // Host: transition from assigning overlay to drawing phase
  const startDrawing = useCallback(async () => {
    const duration = game.zoneDuration || game.timerDuration || 60;
    await firebase.updateGameState({
      phase: 'drawing',
      timerDuration: duration,
      timerEndsAt: Date.now() + (duration * 1000),
    });
  }, [firebase, game.zoneDuration, game.timerDuration]);

  // Host: advance to next zone or reveal
  const advanceZone = useCallback(async () => {
    if (advancingRef.current) return;
    advancingRef.current = true;

    try {
      const currentZone = game.currentZone ?? 0;
      const currentCreature = game.currentCreature ?? 0;
      const maxZone = zoneCount - 1;

      if (currentZone < maxZone) {
        // Move to next zone — show assigning overlay
        const nextZone = currentZone + 1;
        const nextDrawer = game.creatures?.[currentCreature]?.zones?.[nextZone]?.assignedTo;

        await firebase.updateGameState({
          phase: 'assigning',
          currentZone: nextZone,
          currentDrawerId: nextDrawer || null,
          timerEndsAt: null,
        });
      } else {
        // All zones for this creature complete — reveal
        await firebase.updateGameState({
          phase: 'reveal',
          timerEndsAt: null,
          currentDrawerId: null,
        });
      }
    } finally {
      advancingRef.current = false;
    }
  }, [firebase, game.currentZone, game.currentCreature, game.creatures, zoneCount]);

  // Host: advance from reveal to next creature or gallery
  const advanceFromReveal = useCallback(async () => {
    const currentCreature = game.currentCreature ?? 0;
    const totalCreatures = game.totalCreatures ?? 1;

    if (currentCreature < totalCreatures - 1) {
      // More creatures to go — clear canvas, start next creature
      const nextCreature = currentCreature + 1;
      const nextDrawer = game.creatures?.[nextCreature]?.zones?.[0]?.assignedTo;

      await firebase.clearAllStrokes();
      dispatch({ type: 'CLEAR_ALL_STROKES' });

      await firebase.updateGameState({
        phase: 'assigning',
        currentCreature: nextCreature,
        currentZone: 0,
        currentDrawerId: nextDrawer || null,
        timerEndsAt: null,
      });
    } else {
      // All creatures done — gallery time
      await firebase.updateGameState({
        phase: 'gallery',
        timerEndsAt: null,
        currentDrawerId: null,
      });
    }
  }, [firebase, dispatch, game.currentCreature, game.totalCreatures, game.creatures]);

  // Host: play again with fresh setup
  const playAgain = useCallback(async () => {
    await firebase.clearAllStrokes();
    dispatch({ type: 'CLEAR_ALL_STROKES' });

    const memberIds = Object.keys(room.members || {});
    const { creatures, totalCreatures } = assignZones(memberIds, room.members || {}, zoneCount);

    await firebase.updateGameState({
      phase: 'lobby',
      creatures,
      totalCreatures,
      currentCreature: 0,
      currentZone: 0,
      currentDrawerId: null,
      timerEndsAt: null,
    });
  }, [firebase, dispatch, room.members, zoneCount]);

  // Host: end game entirely
  const endGame = useCallback(async () => {
    await firebase.clearGame();
    dispatch({ type: 'CLEAR_GAME' });
    dispatch({ type: 'ADD_TOAST', payload: { message: 'Game ended — back to free draw' } });
  }, [firebase, dispatch]);

  // Host-driven timer — drawing phase
  useEffect(() => {
    if (!user.isHost || game.phase !== 'drawing' || !game.timerEndsAt) return;
    const remaining = game.timerEndsAt - Date.now();
    if (remaining <= 0) {
      advanceZone();
      return;
    }
    const timeout = setTimeout(advanceZone, remaining);
    return () => clearTimeout(timeout);
  }, [user.isHost, game.phase, game.timerEndsAt, advanceZone]);

  // Host-driven assigning overlay — auto-advance after 3s
  useEffect(() => {
    if (!user.isHost || game.phase !== 'assigning') return;
    const timeout = setTimeout(startDrawing, 3000);
    return () => clearTimeout(timeout);
  }, [user.isHost, game.phase, startDrawing]);

  // Host-driven reveal — auto-advance after 5s
  useEffect(() => {
    if (!user.isHost || game.phase !== 'reveal') return;
    const timeout = setTimeout(advanceFromReveal, 6000);
    return () => clearTimeout(timeout);
  }, [user.isHost, game.phase, advanceFromReveal]);

  return {
    initCorpse,
    startCorpse,
    startDrawing,
    advanceZone,
    advanceFromReveal,
    playAgain,
    endGame,
  };
}
